import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  companyLogoUrl,
  getCompany,
  getSetup,
  setSetupDismissed,
  updateCompany,
  uploadCompanyLogo,
  type Company as CompanyProfile,
  type Me,
  type SetupState,
} from '../api.js';
import { navigate } from '../router.js';
import { Roles } from './Roles.js';
import { Templates } from './Templates.js';
import { Thresholds } from './Thresholds.js';

/**
 * The company itself (AD-01), and the setup checklist that sits on top of it
 * (ID-06).
 *
 * WHY A CHECKLIST AND NOT A WIZARD
 * --------------------------------
 * J1 asks for a five-step wizard, "skippable and resumable". A checklist IS
 * that, and is better at the "resumable" half: there is no order to be in the
 * middle of, each item links to the screen where the work already happens, and
 * closing the browser loses nothing because nothing is half-entered anywhere.
 *
 * Each item's state is worked out from the company's own data, so it cannot
 * claim something is done that is not.
 *
 * WHAT IS NOT HERE, AND WHY
 * -------------------------
 * J1's other two steps — starting templates and notification defaults — have no
 * machinery behind them yet. Offering them would tell a new customer they had
 * configured something they had not, which is worse than a shorter list.
 *
 * The trading name, timezone and currency were set when the company was created,
 * so they are never the missing piece; they are editable here because a company
 * that answered wrong on day one should not have to be recreated.
 */

/**
 * A short list, not every IANA zone.
 *
 * A free-text box gets "Dubai" typed into it, which is refused, which reads as
 * the product being fussy. The Gulf plus the places a UAE contractor's head
 * office tends to be. Anything else goes through the operator.
 */
const TIMEZONES = [
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Europe/London',
];

const CURRENCIES = ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'USD', 'EUR', 'GBP'];

interface Props {
  readonly me: Me;
}

export function Company({ me }: Props): JSX.Element {
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [profile, state] = await Promise.all([getCompany(), getSetup()]);
      setCompany(profile);
      setSetup(state);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load your company.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (denied) {
    return (
      <section>
        <h2>Company</h2>
        <p className="hint">Your role does not include your company&rsquo;s details.</p>
      </section>
    );
  }

  return (
    <section>
      <div className="section-head">
        <h2>Company</h2>
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {setup && (
        <SetupChecklist
          setup={setup}
          canDismiss={me.can.edit_company}
          busy={busy}
          onToggle={async (dismissed) => {
            setBusy(true);
            try {
              setSetup(await setSetupDismissed(dismissed));
            } catch (caught) {
              setError(caught instanceof ApiError ? caught.message : 'That did not work.');
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      <h3 className="sub-head">Details</h3>

      {company === null && !error && <p aria-live="polite">Loading…</p>}

      {company && !me.can.edit_company && <ReadOnlyProfile company={company} />}

      {company && me.can.edit_company && (
        <ProfileForm
          company={company}
          busy={busy}
          saved={saved}
          onSubmit={async (input) => {
            setBusy(true);
            setError(null);
            setSaved(false);
            try {
              setCompany(await updateCompany(input));
              setSetup(await getSetup());
              setSaved(true);
            } catch (caught) {
              setError(caught instanceof ApiError ? caught.message : 'That did not save.');
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {company && (
        <LogoSection
          company={company}
          canEdit={me.can.edit_company}
          busy={busy}
          onUploaded={(next) => setCompany(next)}
          onError={(message) => setError(message)}
          setBusy={setBusy}
        />
      )}

      {me.can.view_templates && <Templates canManage={me.can.manage_templates} />}

      {/* Above Roles, below the profile. It is read far more often than it is
          written — it is where somebody goes to find out what "late" means in
          this company — and reading it needs no admin rights. */}
      <Thresholds canEdit={me.can.edit_company} />

      {/* Last on the page on purpose. It is the most consequential thing here
          and the least often needed — an admin edits the VAT number more often
          than they redraw who may see the margin. */}
      {me.can.view_roles && <Roles canManage={me.can.manage_roles} />}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface LogoProps {
  readonly company: CompanyProfile;
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly onUploaded: (company: CompanyProfile) => void;
  readonly onError: (message: string) => void;
  readonly setBusy: (busy: boolean) => void;
}

/**
 * The logo is a company DOCUMENT — versioned and checksummed like every other
 * file — so this section only points at it. The `key` on the img makes the
 * browser refetch after a replacement; the URL itself never changes.
 */
function LogoSection({ company, canEdit, busy, onUploaded, onError, setBusy }: LogoProps): JSX.Element {
  return (
    <>
      <h3 className="sub-head">Logo</h3>
      {company.logo_document_id ? (
        <img
          key={company.logo_document_id}
          src={companyLogoUrl()}
          alt={`${company.name} logo`}
          style={{ maxHeight: '80px', maxWidth: '240px' }}
        />
      ) : (
        <p className="hint">
          No logo yet.{' '}
          {canEdit
            ? 'It appears on what outside parties see — a PNG, JPEG or WebP.'
            : 'A company admin can add one.'}
        </p>
      )}
      {canEdit && (
        <div className="button-row">
          <label htmlFor="co-logo" className="link-button file-button">
            {busy ? 'Uploading…' : company.logo_document_id ? 'Replace the logo' : 'Add a logo'}
          </label>
          <input
            id="co-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="visually-hidden"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setBusy(true);
              onError('');
              void uploadCompanyLogo(file)
                .then((next) => onUploaded(next))
                .catch((caught) =>
                  onError(caught instanceof ApiError ? caught.message : 'That did not work.'),
                )
                .finally(() => setBusy(false));
              event.target.value = '';
            }}
          />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface ChecklistProps {
  readonly setup: SetupState;
  readonly canDismiss: boolean;
  readonly busy: boolean;
  readonly onToggle: (dismissed: boolean) => void | Promise<void>;
}

function SetupChecklist({ setup, canDismiss, busy, onToggle }: ChecklistProps): JSX.Element | null {
  const done = setup.steps.length - setup.remaining;

  if (setup.complete && setup.dismissed) return null;

  return (
    <div className="panel">
      <p className="panel-title">
        Setting up{' '}
        <span className="hint">
          — {done} of {setup.steps.length} done
        </span>
      </p>

      {setup.complete ? (
        <p>Everything on this list is done. You can put it away.</p>
      ) : (
        <p className="hint">
          Nothing here is compulsory and you can come back to it. It is the short list of things
          that make the rest of the app work properly.
        </p>
      )}

      <ul className="setup-steps">
        {setup.steps.map((step) => (
          <li key={step.key} className={step.done ? 'setup-step done' : 'setup-step'}>
            {/* Never colour alone: the word and the mark both say it. */}
            <span className="setup-mark" aria-hidden="true">
              {step.done ? '✓' : '○'}
            </span>
            <span className="setup-body">
              <strong>{step.title}</strong>
              <span className="visually-hidden">{step.done ? ' — done' : ' — still to do'}</span>
              <span className="hint"> {step.why}</span>
            </span>
            {!step.done && (
              <a
                href={step.href}
                className="link-button"
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                  event.preventDefault();
                  navigate(step.href);
                }}
              >
                Do it
                <span className="visually-hidden"> — {step.title}</span>
              </a>
            )}
          </li>
        ))}
      </ul>

      {canDismiss && (
        <div className="button-row">
          <button
            type="button"
            className="secondary inline"
            disabled={busy}
            onClick={() => void onToggle(!setup.dismissed)}
          >
            {setup.dismissed ? 'Show this list again' : 'Put this list away'}
          </button>
          {setup.dismissed && (
            <p className="hint">
              It is hidden everywhere else. This page always shows it, so it is never lost.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReadOnlyProfile({ company }: { readonly company: CompanyProfile }): JSX.Element {
  return (
    <>
      <table className="grid">
        <caption className="visually-hidden">Your company&rsquo;s details</caption>
        <tbody>
          <tr>
            <th scope="row">Name</th>
            <td>{company.name}</td>
          </tr>
          <tr>
            <th scope="row">Legal name</th>
            <td>{company.legal_name ?? '—'}</td>
          </tr>
          <tr>
            <th scope="row">Trade licence</th>
            <td>{company.trade_licence_no ?? '—'}</td>
          </tr>
          <tr>
            <th scope="row">Timezone</th>
            <td>{company.timezone}</td>
          </tr>
          <tr>
            <th scope="row">Currency</th>
            <td>{company.default_currency}</td>
          </tr>
          <tr>
            <th scope="row">VAT</th>
            <td>{company.vat_rate}%</td>
          </tr>
        </tbody>
      </table>
      <p className="hint">Your role does not include changing these.</p>
    </>
  );
}

// ---------------------------------------------------------------------------

interface FormProps {
  readonly company: CompanyProfile;
  readonly busy: boolean;
  readonly saved: boolean;
  readonly onSubmit: (input: {
    name: string;
    legal_name: string | null;
    trade_licence_no: string | null;
    timezone: string;
    default_currency: string;
    vat_rate: string;
  }) => void | Promise<void>;
}

function ProfileForm({ company, busy, saved, onSubmit }: FormProps): JSX.Element {
  const [name, setName] = useState(company.name);
  const [legalName, setLegalName] = useState(company.legal_name ?? '');
  const [licence, setLicence] = useState(company.trade_licence_no ?? '');
  const [timezone, setTimezone] = useState(company.timezone);
  const [currency, setCurrency] = useState(company.default_currency);
  const [vat, setVat] = useState(company.vat_rate);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!name.trim()) return;
    void onSubmit({
      name: name.trim(),
      legal_name: legalName.trim() || null,
      trade_licence_no: licence.trim() || null,
      timezone,
      default_currency: currency,
      vat_rate: vat.trim(),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="co-name">Name</label>
          <input
            id="co-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            disabled={busy}
          />
          <p className="hint">What you call yourselves day to day.</p>
        </div>
        <div className="field">
          <label htmlFor="co-legal">Legal name</label>
          <input
            id="co-legal"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            maxLength={300}
            disabled={busy}
            placeholder="Your Company L.L.C."
          />
          <p className="hint">As it appears on the trade licence.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="co-licence">Trade licence number</label>
          <input
            id="co-licence"
            value={licence}
            onChange={(e) => setLicence(e.target.value)}
            maxLength={100}
            disabled={busy}
            placeholder="CN-1234567"
          />
        </div>
        <div className="field">
          <label htmlFor="co-vat">VAT rate</label>
          <input
            id="co-vat"
            value={vat}
            onChange={(e) => setVat(e.target.value)}
            maxLength={10}
            disabled={busy}
            inputMode="decimal"
          />
          <p className="hint">A percentage, so 5 for five per cent.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="co-tz">Timezone</label>
          <select
            id="co-tz"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            disabled={busy}
          >
            {(TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
          <p className="hint">
            This decides what &ldquo;today&rdquo; means — whether a diary is missing, whether an
            approval is late. <strong>Projects already running keep the timezone they started
            in</strong>; only new ones follow this.
          </p>
        </div>
        <div className="field">
          <label htmlFor="co-currency">Currency</label>
          <select
            id="co-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={busy}
          >
            {(CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES]).map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
          <p className="hint">The default for a new project. A project can differ.</p>
        </div>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {saved && (
          <p className="hint" role="status">
            Saved.
          </p>
        )}
      </div>
    </form>
  );
}
